import { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface CommitThreadProps {
  children: ReactNode[];
  hasMore?: boolean;
}

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export default function CommitThread({ children, hasMore = false }: CommitThreadProps) {
  const validChildren = children.filter(Boolean);

  return (
    <div className="relative" role="feed" aria-label="Commits">
      {validChildren.map((child, index) => {
        const isLast = index === validChildren.length - 1;
        // Show marquee only on last item when there's potentially more to load
        const showMarquee = isLast && hasMore;

        return (
          <motion.article
            key={index}
            className="relative flex gap-0 sm:gap-4"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            custom={index}
            aria-posinset={index + 1}
            aria-setsize={validChildren.length}
          >
            {/* Thread line connector - hidden on mobile */}
            <div className="hidden sm:flex flex-col items-center w-4 flex-shrink-0" aria-hidden="true">
              {/* Dot */}
              <div className="w-2 h-2 rounded-full bg-[hsl(var(--muted-foreground))]/40 mt-5 z-10" />
              {/* Vertical line - solid for non-last, marquee for last when more available */}
              {showMarquee ? (
                <div className="w-[2px] flex-1 -mb-1 overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--border))] via-transparent to-[hsl(var(--border))] bg-[length:100%_12px] animate-[marquee-down_1.5s_linear_infinite]" />
                </div>
              ) : !isLast ? (
                <div className="w-[2px] flex-1 bg-[hsl(var(--border))] -mb-1" />
              ) : null}
            </div>
            {/* Commit card */}
            <div className="flex-1 min-w-0 pb-3 sm:pb-4">
              {child}
            </div>
          </motion.article>
        );
      })}
    </div>
  );
}
